import { FC, useEffect, useRef, useState } from "react";
// import axios from "axios";
import {
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Resolver, useWatch } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  PhoneInput,
  defaultCountries,
  parseCountry,
} from "react-international-phone";
import QrCodeIcon from "@mui/icons-material/QrCode";
import ReceiptIcon from "@mui/icons-material/Receipt";
import PaidIcon from "@mui/icons-material/Paid";
import BrowserUpdatedIcon from "@mui/icons-material/BrowserUpdated";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";

import { CustomButton, CustomTextBox, PosLayout } from "../../../components";
import { CustomCheckBox } from "../../../components/atoms/checkbox/custom-checkbox";
import { CustomSelectBox } from "../../../components/atoms/custom-select-box/custom-select-box";
import { HeaderTitle } from "../../../components/atoms/header-title/header-title";
import { MultiTabSelector } from "../../../components/atoms/multi-tab-selector/multi-tab-selector";
import { Panel } from "../../../components/common/panel/panel";
import { PosInnerPageLayout } from "../../../components/layouts/pos-layout/PosInnerPageLayout";
import { Colors } from "../../../configs";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  createCustomerAction,
  getCustomerByIdAction,
  getUserNotesByIdAction,
  updateCustomerAction,
} from "../../../redux/actions/customerAction";
import StoreCreditsModal from "../../../components/pos/store-credits-modal/store-credits-modal";
import { CustomerSliceAction } from "../../../redux/slice/customerSlice";

import "react-international-phone/style.css";
import { handleTabAndEnter } from "../../../util/common-functions";
import UserNotesModal from "../../../components/pos/notes-modal/userNotesModal";
import TaxExceptionModal from "../../../components/pos/tax-exception-modal/TaxExceptionModal";
import CardInformationModal from "../../../components/pos/card-information-modal/CardInformationModal";
import UpdateCardsModal from "../../../components/pos/update-cards-modal/UpdateCardsModal";
import { states } from "../../../typings/enum/USA-states";
import { useUserNote } from "../../../providers/UserNoteProvider";
import { NoteOccasion } from "../../../typings/enum/noteOccasions";
import ConfirmDialog from "../../../components/atoms/confirm-box/confirm-box";
import {
  ICreateCustomerRequest,
  IUpdateCustomerRequest,
  IWaiverSignature,
} from "../../../typings/interfaces/requests/customer-requests";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import IOSSwitch from "../../../components/atoms/custom-toggle-button/custom-toggle-button";
import PrintCustomerTagModal from "../../../components/pos/print-customer-tag-modal/PrintCustomerTagModal";
import { pxToRem } from "../../../util";
import AssignNewRoute from "../../../components/common/AssignNewRoute";
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";
import { openWaiverPrintWindow } from "../../../components/pos/WaiverPrint/WaiverPrintPage";
import AddressWaiverViewModal from "../../../components/pos/address-waiver-view-modal/AddressWaiverViewModal";
import SignatureCaptureModal from "../../../components/pos/signature-capture-modal/SignatureCaptureModal";
import WarningDialog from "../../../components/atoms/warning-dialog/WarningDialog";
import {
  CustomerReleaseSignatureResponse,
  getCustomerLatestReleaseSignatureAction,
  getReleaseByNameAction,
} from "../../../redux/actions/customerReleaseSignatureAction";

const isAgeRelatedDiscount = (discountId: string, discounts: any[]) => {
  if (!discountId || discountId === "none" || !discounts) return false;
  const discount = discounts.find((d) => d.id === discountId);
  if (!discount) return false;
  const ageRelatedKeywords = ["senior"];
  const discountName = discount.discountName?.toLowerCase() || "";

  return ageRelatedKeywords.some((keyword) => discountName.includes(keyword));
};

const schema = Yup.object().shape({
  id: Yup.string().notRequired(),
  createdTime: Yup.string().notRequired(),
  firstName: Yup.string().required(),
  middleName: Yup.string(),
  lastName: Yup.string().required(),
  mobilePhone: Yup.string()
    .test(
      "has-11-digits",
      "Mobile phone number must contain exactly 11 digits",
      (value) => {
        if (!value) return false;
        const digits = value.replace(/\D/g, "");
        return digits.length === 11;
      }
    )
    .required(),
  landPhone: Yup.string()
    .test(
      "has-11-digits-or-empty",
      "Land phone number must contain exactly 11 digits or be empty",
      (value) => {
        if (!value) return true;
        const digits = value.replace(/\D/g, "");

        if (digits.length <= 1) return true;
        return digits.length === 11;
      }
    )
    .test(
      "not-same-as-mobile",
      "Home phone cannot be the same as cell phone",
      function (value) {
        const { mobilePhone } = this.parent;
        if (!value || !mobilePhone) return true;
        const landDigits = value.replace(/\D/g, "");
        const mobileDigits = mobilePhone.replace(/\D/g, "");
        if (landDigits.length !== 11 || mobileDigits.length !== 11) return true;
        return landDigits !== mobileDigits;
      }
    )
    .optional(),
  email: Yup.string().email(),
  paymentPreference: Yup.string().notRequired(),
  payAt: Yup.string().notRequired(),
  enableDelivery: Yup.boolean().notRequired().default(false),

  // personal address
  notWillingToProvide: Yup.boolean(),
  personalAddressLine1: Yup.string().when("notWillingToProvide", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.required(
        "Either provide personal address or check 'Not Willing To Provide'"
      ),
  }),
  personalAddressLine2: Yup.string().notRequired(),
  personalCity: Yup.string().when("notWillingToProvide", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.required(
        "Either provide personal address or check 'Not Willing To Provide'"
      ),
  }),
  personalState: Yup.string().when("notWillingToProvide", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.required(
        "Either provide personal address or check 'Not Willing To Provide'"
      ),
  }),
  personalZip: Yup.string()
    .nullable()
    .when("notWillingToProvide", {
      is: true,
      then: (schema) => schema.notRequired(),
      otherwise: (schema) =>
        schema
          .matches(/^[0-9]{5}$/, "Zip code must be 5 digits")
          .required(
            "Either provide personal address or check 'Not Willing To Provide'"
          ),
    }),

  //   billingAddressLine1: Yup.string().test(
  //     "billingAddressRequired",
  //     "Either provide billing address or check 'Same as Personal Address'",
  //     function (value) {
  //       const { billingSameAsPersonalAddress, notWillingToProvide, billingCity, billingState, billingZip } = this.parent;
  //       const isBillingFilled = value && billingCity && billingState && billingZip;
  //       return notWillingToProvide || billingSameAsPersonalAddress || isBillingFilled;
  //     }
  //   ),

  // billingAddressLine2: Yup.string().notRequired(),
  // billingCity: Yup.string().notRequired(),
  // billingState: Yup.string().notRequired(),
  // billingZip: Yup.string()
  //   .nullable()
  //   .notRequired()
  //   .test(
  //     "empty-or-valid",
  //     "Zip code must be exactly 5 digits and numeric",
  //     (value) => {
  //       if (!value) return true;
  //       return /^[0-9]{5}$/.test(value);
  //     }
  //   ),

  // billing address
  billingSameAsPersonalAddress: Yup.boolean(),
  billingAddressLine1: Yup.string().when(
    ["notWillingToProvide", "billingSameAsPersonalAddress"],
    {
      is: (
        notWillingToProvide: boolean,
        billingSameAsPersonalAddress: boolean
      ) => !notWillingToProvide && !billingSameAsPersonalAddress,
      then: (schema) => schema.required("Billing Address Line 1 is required"),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
  billingAddressLine2: Yup.string().notRequired(),
  billingCity: Yup.string().when(
    ["notWillingToProvide", "billingSameAsPersonalAddress"],
    {
      is: (
        notWillingToProvide: boolean,
        billingSameAsPersonalAddress: boolean
      ) => !notWillingToProvide && !billingSameAsPersonalAddress,
      then: (schema) => schema.required("Billing City is required"),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
  billingState: Yup.string().when(
    ["notWillingToProvide", "billingSameAsPersonalAddress"],
    {
      is: (
        notWillingToProvide: boolean,
        billingSameAsPersonalAddress: boolean
      ) => !notWillingToProvide && !billingSameAsPersonalAddress,
      then: (schema) => schema.required("Billing State is required"),
      otherwise: (schema) => schema.notRequired(),
    }
  ),
  billingZip: Yup.string()
    .nullable()
    .when(["notWillingToProvide", "billingSameAsPersonalAddress"], {
      is: (
        notWillingToProvide: boolean,
        billingSameAsPersonalAddress: boolean
      ) => !notWillingToProvide && !billingSameAsPersonalAddress,
      then: (schema) =>
        schema
          .matches(
            /^[0-9]{5}$/,
            "Zip code must be exactly 5 digits and numeric"
          )
          .required("Billing Zip is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

  // miscellaneous
  priceList: Yup.string(),
  dob: Yup.string()
    .test(
      "valid-date-format",
      "Date must be in MM/DD/YYYY format with valid month, day, and year",
      (value) => {
        //if (!value) return false;
        if (!value) return true;

        if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
          return false;
        }

        const [month, day, year] = value.split("/").map(Number);

        if (year < 1900 || year > new Date().getFullYear()) {
          return false;
        }

        if (month < 1 || month > 12) {
          return false;
        }

        const daysInMonth = new Date(year, month, 0).getDate();
        if (day < 1 || day > daysInMonth) {
          return false;
        }

        const dateObj = new Date(year, month - 1, day);
        return (
          dateObj.getDate() === day &&
          dateObj.getMonth() === month - 1 &&
          dateObj.getFullYear() === year
        );
      }
    )
    .notRequired(),
  //.required("Date of birth is required"),

  discount: Yup.string(),
  subEmailPromotions: Yup.boolean(),
  subEmailMonthlyStatements: Yup.boolean(),
  subEmailInvoices: Yup.boolean(),
  subMiscellaneous: Yup.boolean(),

  // preferences
  preferenceStarch: Yup.array(Yup.string()),
  preferenceShirt: Yup.array(Yup.string()),
  pickupPref: Yup.array(Yup.string()),
  pantsPref: Yup.array(Yup.string()),
  customerTypePref: Yup.array(Yup.string())
    .min(1, "At least one customer type must be selected")
    .required(),
  customerPayAtPref: Yup.array(Yup.string())
    .min(1, "At least one customer type must be selected")
    .required(),
  paymentPreferencePref: Yup.array(Yup.string())
    .min(1, "At least one customer type must be selected")
    .required(),
  isDeliveryEnabled: Yup.boolean(),
  customerBagPref: Yup.array(Yup.string()),
  automaticAlteration: Yup.boolean().notRequired(),
  allowedAmount: Yup.string().when("automaticAlteration", {
    is: true,
    then: (schema) => schema.required("Allowed Amount is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

// to do: modify the schema

type FormData = Yup.InferType<typeof schema>;

// Define the resolver options
const resolver: Resolver<FormData> = yupResolver(schema);

const phoneCountries = defaultCountries.filter((country) => {
  const { iso2 } = parseCountry(country);
  return ["us"].includes(iso2);
});

const PanelLayout: FC<{
  title: string;
  children: React.ReactNode;
  headerRight?: React.ReactNode;
}> = (props) => {
  const { title, children, headerRight } = props;
  return (
    <Panel>
      <Box
        sx={{
          padding: "2rem",
          height: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: "1rem",
          }}
        >
          <HeaderTitle variation="panel_header" title={title} />
          {headerRight}
        </Box>
        <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}>{children}</Box>
      </Box>
    </Panel>
  );
};

export interface CreateCustomerPageProps {
  mode?: "edit" | "create";
}

const CreateCustomerPage: FC<CreateCustomerPageProps> = ({
  mode = "create",
}) => {
  const params = useParams();
  const customerId = params.id;
  const dispatch = useAppDispatch();
  const { customer } = useAppSelector((state) => state.customer);
  const taxEnabled =
    useAppSelector(
      (state) => state.merchant.merchantListResponse?.body?.[0]?.taxEnabled
    ) ?? false;
  const { getDiscountByStoreResponse } = useAppSelector(
    (state) => state.discount
  );
  const { user } = useAppSelector((state) => state.auth);
  const { activeStation } = useAppSelector((state) => state.cashierStation);
  const normalizeDiscount = (v?: string) => (!v || v === "none" ? "" : v);
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [openNotesModal, setOpenNotesModal] = useState<boolean>(false);
  const [openStoreCreditsModal, setOpenStoreCreditsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);
  const [isDirtyState, setIsDirtyState] = useState(false);
  const [isTaxModalOpen, setIsTaxModalOpen] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isCardInformationModalOpen, setIsCardInformationModalOpen] =
    useState(false);
  const [isUpdateCardsModalOpen, setIsUpdateCardsModalOpen] = useState(false);
  const [isWaiverViewModalOpen, setIsWaiverViewModalOpen] = useState(false);
  const [isSignatureModalOpen, setIsSignatureModalOpen] = useState(false);
  const [capturedSignature, setCapturedSignature] = useState<IWaiverSignature | null>(null);
  const [existingSignature, setExistingSignature] = useState<CustomerReleaseSignatureResponse | null>(null);
  const [agreementText, setAgreementText] = useState<string | undefined>(undefined);
  const [isDisagreeModalOpen, setIsDisagreeModalOpen] = useState(false);
  const [mgrOpen, setMgrOpen] = useState(false);
  const [pendingType, setPendingType] = useState<
    "RETAIL" | "BUSINESS" | "EMPLOYEE" | null
  >(null);

  const [isAgeRelatedDiscountSelected, setIsAgeRelatedDiscountSelected] =
    useState(false);

  const isRestricted = (v: string) => v === "BUSINESS" || v === "EMPLOYEE";

  const requireManager = (val: string) =>
    val === "BUSINESS" || val === "EMPLOYEE";

  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isDobInvalid, setIsDobInvalid] = useState(false);

  const { showNote } = useUserNote();
  const { showSnackbar } = useSnackbarContext();

  const handleOpenUpdateCardsModal = () => {
    setIsUpdateCardsModalOpen(true);
  };

  const handleCloseUpdateCardsModal = () => {
    setIsUpdateCardsModalOpen(false);
  };

  const handleOpenCardInformationModal = () => {
    setIsCardInformationModalOpen(true);
  };

  const handleCloseCardInformationModal = () => {
    setIsCardInformationModalOpen(false);
  };

  const handleOpenPrintModal = () => {
    setIsPrintModalOpen(true);
  };

  const handleClosePrintModal = () => {
    setIsPrintModalOpen(false);
  };

  const handleOpenTaxModal = () => {
    setIsTaxModalOpen(true);
  };

  const handleCloseTaxModal = () => {
    setIsTaxModalOpen(false);
  };

  const handleOnCloseStoreCreditsModal = () => {
    setOpenStoreCreditsModal(false);
  };

  const handleOnNotesModalClose = () => setOpenNotesModal(false);

  // ref array
  // make inputRef array with 42 ref

  useEffect(() => {
    return () => {
      dispatch(CustomerSliceAction.clearCustomer());
    };
  }, []);

  const inputRef = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  useEffect(() => {
    if (mode === "create") {
      const currentValues = getValues();
      currentValues.customerTypePref = ["RETAIL"];
      reset(currentValues, { keepDirty: false });
      // setValue("customerTypePref", ["RETAIL"], { shouldDirty: false });
      setValue("personalState", "", { shouldDirty: false });
      setValue("billingState", "", { shouldDirty: false });
    }
  }, []);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === "discount") {
        const currentDiscount = value.discount || "";
        const discounts = getDiscountByStoreResponse?.body || [];
        const isAgeRelated = isAgeRelatedDiscount(currentDiscount, discounts);
        setIsAgeRelatedDiscountSelected(isAgeRelated);

        const currentDob = value.dob || "";
        const isValidFormat = validateDobFormat(currentDob);

        const shouldShowError = Boolean(
          (isAgeRelated && (!currentDob || !isValidFormat)) ||
          (currentDob && currentDob.length > 0 && !isValidFormat)
        );
        setIsDobInvalid(shouldShowError);
      }
    });

    return () => subscription.unsubscribe();
  }, [getDiscountByStoreResponse]);

  const formatDateToMDY = (isoString?: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    return `${month}/${day}/${year}`;
  };

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors, isValid, isDirty, dirtyFields },
    clearErrors,
    setValue,
    getValues,
    watch,
    trigger,
  } = useForm<FormData>({
    defaultValues:
      mode === "create"
        ? {
          personalState: "",
          billingState: "",
          customerTypePref: ["RETAIL"],
          customerPayAtPref: ["COUNTER"],
          paymentPreferencePref: ["DROP_OFF"],
          isDeliveryEnabled: false,
          notWillingToProvide: false,
          discount: "none",
          automaticAlteration: false,
          allowedAmount: "",
        }
        : ({
          ...customer,
          personalState: customer?.personalState ?? states.ALABAMA,
          billingState: "",
          customerPayAtPref: customer?.paymentPreference ?? "COUNTER",
          paymentPreferencePref: customer?.payAt ?? "DROP_OFF",
          isDeliveryEnabled: Boolean(customer?.enableDelivery ?? false),
          notWillingToProvide: customer?.notWillingToProvide ?? false,
          dob: formatDateToMDY(customer?.dob),
          discount: customer?.discount || "none",
        } as any),
    resolver: resolver,
  });

  const changeFormDirtyState = (isDirty: boolean) => {
    const currentValues = getValues();
    reset(currentValues, { keepDirty: isDirty });
  };

  const location = useLocation();
  const fromInactivePopup = location.state?.fromInactivePopup;

  const navigate = useNavigate();
  const handleOnBackClick = () => {
    if (isDirty) {
      setDiscardDialogOpen(true);
    } else {
      redirectPage();
    }
  };
  const handleOnFinish = (userId: string) => {
    navigate(`/pos/${userId}`);
  };
  const redirectPage = () => {
    // if (mode === "create") navigate("/pos/lobby");
    // else navigate(`/pos/${customerId}`);
    navigate(-1);
  };

  useEffect(() => {
    if (customerId) {
      fetchUser();
      dispatch(getUserNotesByIdAction(customerId as string)).then(
        (res: any) => {
          showNote(NoteOccasion.ONVIEW_EDIT, res.payload);
        }
      );
    }
  }, [customerId]);

  const fetchUser = () => {
    if (customerId) dispatch(getCustomerByIdAction(customerId));
  };

  const watchSameAsPersonalAddress = watch(
    "billingSameAsPersonalAddress",
    false
  );

  const notWillingToProvide = watch("notWillingToProvide", false);

  const billingState = watch("billingState");
  const personalState = watch("personalState");

  const onSubmit = async (data: FormData) => {
    trigger();

    if (!isValid) return;
    setIsLoading(true);
    data.landPhone = data.landPhone?.length === 2 ? "" : data.landPhone;
    data.mobilePhone = data.mobilePhone?.length === 2 ? "" : data.mobilePhone;
    data.notWillingToProvide ??= false;

    data.discount = normalizeDiscount(data.discount);
    if (capturedSignature) {
      (data as any).waiverSignature = capturedSignature;
    }
    if (mode === "edit") {
      data.id = customerId;
      data.createdTime = customer?.createdTime;

      // Force status to ACTIVE if coming from inactive popup
      if (fromInactivePopup) {
        (data as any).accountStatus = "ACTIVE";
      }

      // axios.put(UPDATE_CUSTOMER, data).then((res) => {
      //   setIsLoading(false);
      //   // handleOnBackClick();
      //   customerId && handleOnFinish(customerId);
      // });
      const res: any = await dispatch(
        updateCustomerAction(data as IUpdateCustomerRequest)
      ).then((res: any) => {
        setIsLoading(false);
        // res.payload.body.id && handleOnFinish(res.payload.body.id);

        res.payload.body?.id
          ? handleOnFinish(res.payload.body?.id)
          : showSnackbar("Failed to update customer", "error");
      });

      showSnackbar("Customer has updated successfully", "success");
    } else {
      // axios.post(CREATE_CUSTOMER, data).then((res) => {
      //   setIsLoading(false);
      //   // handleOnBackClick();
      //   res.data.body?.id
      //     ? handleOnFinish(res.data.body.id)
      //     : null
      // });\

      dispatch(createCustomerAction(data as ICreateCustomerRequest)).then(
        (res: any) => {
          setIsLoading(false);
          // res.payload.body.id && handleOnFinish(res.payload.body.id);

          if (res.payload.body?.id) {
            handleOnFinish(res.payload.body?.id);
            showSnackbar("Customer has created successfully", "success");
          } else {
            showSnackbar("Failed to create customer", "error");
          }
        }
      );
    }
  };

  const updateBillingAddress = () => {
    if (getValues("billingSameAsPersonalAddress")) {
      setValue("billingAddressLine1", getValues("personalAddressLine1") || "");
      setValue("billingAddressLine2", getValues("personalAddressLine2") || "");
      setValue("billingCity", getValues("personalCity") || "");
      setValue("billingState", getValues("personalState") || states.ALABAMA);
      setValue("billingZip", getValues("personalZip") || "");
      clearErrors([
        "billingAddressLine1",
        "billingAddressLine2",
        "billingCity",
        "billingState",
        "billingZip",
      ]);
    } else {
      setValue("billingAddressLine1", "");
      setValue("billingAddressLine2", "");
      setValue("billingCity", "");
      setValue("billingState", "");
      setValue("billingZip", "");
    }
  };

  const handleNotWillingToProvideChange = (isChecked: boolean) => {
    if (isChecked) {
      setValue("personalAddressLine1", "");
      setValue("personalAddressLine2", "");
      setValue("personalCity", "");
      setValue("personalState", "");
      setValue("personalZip", "");

      setValue("billingAddressLine1", "");
      setValue("billingAddressLine2", "");
      setValue("billingCity", "");
      setValue("billingState", "");
      setValue("billingZip", "");
    }
  };

  const handleSignatureCaptured = (dataUrl: string) => {
    const customerName = `${getValues("firstName") || ""} ${getValues("lastName") || ""}`.trim();
    const customerPhone = `${getValues("mobilePhone") || ""}`.trim();

    const record: IWaiverSignature = {
      imageBase64: dataUrl,
      signedAt: new Date().toISOString(),
      customerName: customerName || "Unknown",
      customerPhone,
      witnessId: user?.id ?? 0,
      witnessName: user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown",
      witnessEmail: user?.email ?? "",
      cashierPointId: activeStation?.cashierPoint?.id ?? null,
      cashierPointName: activeStation?.cashierPoint?.name ?? null,
      storeId: user?.storeId ?? 0,
      purpose: "ADDRESS_WAIVER",
    };

    setCapturedSignature(record);
    setIsSignatureModalOpen(false);
    handleNotWillingToProvideChange(true);
    trigger("notWillingToProvide");
    openWaiverPrintWindow({
      customerName: customerName || "Customer",
      customerPhone,
      signatureDataUrl: dataUrl,
    });
  };

  const handleSignatureModalClose = () => {
    setIsSignatureModalOpen(false);
    setValue("notWillingToProvide", false);
    trigger("notWillingToProvide");
  };

  useEffect(() => {
    updateBillingAddress();
  }, [watchSameAsPersonalAddress]);

  const handleOnChangeMobile = (value: string) => {
    setMobile(value);
    setValue("mobilePhone", value);
  };

  const handleOnChangeLand = (value: string) => {
    setPhone(value);
    setValue("landPhone", value);
    // console.log(getValues("landPhone"));
  };

  useEffect(() => {
    // set values after customer get values
    if (customer && mode == "edit") {
      setValue("firstName", customer.firstName);
      setValue("middleName", customer.middleName ?? "");
      setValue("lastName", customer.lastName);
      setValue("mobilePhone", customer.mobilePhone ?? "");
      setValue("landPhone", customer.landPhone ?? "");
      setValue("email", customer.email);
      setValue("dob", formatDateToMDY(customer.dob) ?? "");
      // setValue("priceList", customer);
      setValue("discount", customer.discount || "none", { shouldDirty: true });
      setValue("subEmailPromotions", customer.subEmailPromotions ?? false);
      setValue(
        "subEmailMonthlyStatements",
        customer.subEmailMonthlyStatements ?? false
      );
      setValue("subEmailInvoices", customer.subEmailInvoices ?? false);
      setValue("subMiscellaneous", customer.subMiscellaneous ?? false);
      setValue("preferenceStarch", customer.preferenceStarch);
      setValue("preferenceShirt", customer.preferenceShirt);
      setValue("pickupPref", customer.pickupPref);
      setValue("pantsPref", customer.pantsPref);
      setValue("customerTypePref", customer.customerTypePref);
      setValue("customerPayAtPref", customer.customerPayAtPref || ["COUNTER"]);
      setValue(
        "paymentPreferencePref",
        customer.paymentPreferencePref || ["DROP_OFF"]
      );
      setValue("isDeliveryEnabled", Boolean(customer.isDeliveryEnabled), {
        shouldDirty: false,
      });
      setValue("automaticAlteration", Boolean(customer.automaticAlteration ?? false));
      const rawAmount = parseFloat(customer.allowedAmount ?? "");
      setValue("allowedAmount", isNaN(rawAmount) ? "" : rawAmount.toFixed(2));
      // setValue("customerBagPref", customer.customerBagPref);
      setValue("personalAddressLine1", customer.personalAddressLine1);
      setValue("personalAddressLine2", customer.personalAddressLine2);
      setValue("personalCity", customer.personalCity);
      setValue("personalState", customer.personalState ?? states.ALABAMA, {
        shouldDirty: false,
      });
      setValue("personalZip", customer.personalZip);
      setValue("billingAddressLine1", customer.billingAddressLine1);
      setValue("billingAddressLine2", customer.billingAddressLine2);
      setValue("billingCity", customer.billingCity);
      setValue("billingState", customer.billingState ?? states.ALABAMA, {
        shouldDirty: false,
      });
      setValue("billingZip", customer.billingZip);
      setValue(
        "billingSameAsPersonalAddress",
        customer.billingSameAsPersonalAddress
      );
      setValue("notWillingToProvide", customer.notWillingToProvide ?? false);

      setTimeout(() => {
        const currentValues = getValues();
        reset(currentValues, { keepDirty: false });
      }, 0);
    } else {
      reset();
    }
    if (!customer) {
      setValue("personalState", "");
      setValue("billingState", "");
    }
  }, [customer]);

  useEffect(() => {
    if (customer) {
      setValue("customerTypePref", ["RETAIL"]);
    }
  }, []);

  useEffect(() => {
    dispatch(getReleaseByNameAction("ADDRESS_WAIVER"))
      .then((res: any) => {
        const text: string | null = res.payload?.body?.agreementText;
        if (text) setAgreementText(text);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === "edit" && customerId) {
      dispatch(getCustomerLatestReleaseSignatureAction({ customerId: Number(customerId) }))
        .then((res: any) => {
          if (res.payload?.body) {
            setExistingSignature(res.payload.body);
          }
        })
        .catch(() => {});
    }
  }, [mode, customerId]);

  const renderStateMenuItems = () => {
    return Object.keys(states).map((key) => (
      <MenuItem value={states[key as keyof typeof states]} key={key}>
        {states[key as keyof typeof states]}
      </MenuItem>
    ));
  };

  const handleCustomSubmit = () => {
    const isWilling = getValues("notWillingToProvide");

    if (isWilling && mode === "create") {
      alert("Printing Address Consent");
      handleSubmit(onSubmit);
    }
  };

  useEffect(() => {
    if (watch("billingSameAsPersonalAddress")) {
      setValue("billingAddressLine1", getValues("personalAddressLine1") || "");
      setValue("billingAddressLine2", getValues("personalAddressLine2") || "");
      setValue("billingCity", getValues("personalCity") || "");
      setValue("billingState", getValues("personalState") || states.ALABAMA);
      setValue("billingZip", getValues("personalZip") || "");
    }
  }, [
    watch("personalAddressLine1"),
    watch("personalAddressLine2"),
    watch("personalCity"),
    watch("personalState"),
    watch("personalZip"),
    watch("billingSameAsPersonalAddress"),
  ]);

  useEffect(() => {
    if (watch("notWillingToProvide")) {
      setValue("billingSameAsPersonalAddress", false);
    }
  }, [watch("notWillingToProvide")]);

  const [preferenceStatus, setPreferenceStatus] = useState("DROP_OFF");
  const paymentPreference = watch("paymentPreferencePref");

  useEffect(() => {
    if (paymentPreference && paymentPreference.length > 0) {
      setPreferenceStatus(paymentPreference[0] ?? "DROP_OFF");
    } else {
      setPreferenceStatus("DROP_OFF");
    }
  }, [paymentPreference]);

  useEffect(() => {
    if (paymentPreference?.[0] === "DROP_OFF") {
      const payAt = getValues("customerPayAtPref")?.[0];
      if (payAt !== "COUNTER") {
        const wasFormDirty = isDirty;

        setValue("customerPayAtPref", ["COUNTER"], {
          shouldValidate: true,
          shouldDirty: false,
        });

        clearErrors("customerPayAtPref");

        if (!wasFormDirty) {
          setTimeout(() => {
            const currentValues = getValues();
            reset(currentValues, { keepDirty: false });
          }, 0);
        }
      }
    }
  }, [paymentPreference]);

  //useEffect(() => {
  //  trigger();
  // }, []);

  const [isAddressVerified, setIsAddressVerified] = useState<boolean | null>(
    null
  );
  const [isManualOverride, setIsManualOverride] = useState(false);
  const [showAssignRouteModal, setShowAssignRouteModal] = useState(false);

  const [selectedRoute, setSelectedRoute] = useState("Route A");
  const [deliveryDays, setDeliveryDays] = useState("Monday, Tuesday");

  const routeOptions = [
    { label: "Route A", value: "Route A", days: "Monday, Tuesday" },
    { label: "Route B", value: "Route B", days: "Wednesday, Friday" },
    { label: "Route C", value: "Route C", days: "Tuesday, Thursday" },
    { label: "Route D", value: "Route D", days: "Friday, Saturday" },
  ];

  const customTextBoxStyles = {
    backgroundColor: Colors.WHITE,
    borderRadius: "10px",
    width: "100%",
    "& .MuiInputBase-input.Mui-disabled": {
      WebkitTextFillColor: Colors.BLACK,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none !important",
    },
  };

  const watchedPhone = useWatch({
    control,
    name: "mobilePhone",
  });

  const watchedLandPhone = useWatch({
    control,
    name: "landPhone",
  });

  const isSameContactNumber = Boolean(
    watchedPhone &&
    watchedLandPhone &&
    watchedPhone.replace(/\D/g, "").length === 11 &&
    watchedLandPhone.replace(/\D/g, "").length === 11 &&
    watchedPhone.replace(/\D/g, "") === watchedLandPhone.replace(/\D/g, "")
  );

  const isPhoneInvalid =
    (watchedPhone &&
      watchedPhone.trim() !== "" &&
      watchedPhone !== "+1" &&
      watchedPhone.replace(/\D/g, "").length !== 11) ||
    isSameContactNumber;

  const isLandPhoneInvalid =
    (watchedLandPhone &&
      watchedLandPhone.trim() !== "" &&
      watchedLandPhone !== "+1" &&
      watchedLandPhone.replace(/\D/g, "").length !== 11) ||
    isSameContactNumber;

  const capitalizeFirst = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

  const capitalizeEachWord = (s: string) => {
    if (!s) return s;
    return s
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // useEffect(() => {
  //   console.log("Current errors:", errors);
  //   console.log("Invalid fields:", Object.keys(errors));
  //   console.log("Error count:", Object.keys(errors).length);
  // }, [errors]);

  const validateEmail = (email: string | string[]) => {
    if (!email || email.length === 0) return false;
    const hasAt = email.includes("@");
    const hasDot = email.includes(".");
    const atIndex = email.indexOf("@");
    const dotIndex = email.lastIndexOf(".");

    return hasAt && hasDot && dotIndex > atIndex;
  };

  const validateDobFormat = (dob: string) => {
    if (!dob || dob.length === 0) return false;
    const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    return dateRegex.test(dob);
  };

  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const mobilenum = watch("mobilePhone");

  return (
    <PosLayout>
      <PosInnerPageLayout
        title={`${mode === "create" ? "New" : "Update"} Customer`}
        onClickBack={handleOnBackClick}
      >
        <ConfirmDialog
          open={discardDialogOpen}
          title="Discard Confirmation"
          message="If you exit, your changes will be discarted"
          onCancel={() => setDiscardDialogOpen(false)}
          onConfirm={() => redirectPage()}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid
            container
            sx={{
              width: "100%",
              paddingBottom: "2rem",
              mb: "2rem",
            }}
          >
            <Grid
              item
              xs={8}
              sx={{
                width: "100%",
              }}
            >
              <Grid
                container
                sx={{
                  width: "100%",
                }}
              >
                {/* Personal Information */}
                <Grid
                  item
                  xs={6}
                  sx={{ padding: "0.57rem", minHeight: "27rem" }}
                >
                  <PanelLayout title="Personal Information">
                    {/* Name Inputs */}
                    <Grid
                      container
                      sx={{ mt: "1rem", alignItems: "center" }}
                    >
                      <Grid item xs={2} sx={{ mt: "0.14rem" }}>
                        <HeaderTitle variation="field_label" title="Name" />
                      </Grid>
                      <Grid container item xs={10}>
                        <Grid item xs={4} sx={{ pr: "0.57rem" }}>
                          <Controller
                            name="firstName"
                            control={control}
                            rules={{
                              required: {
                                message: "First Name is required",
                                value: true,
                              },
                            }}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeFirst(e.target.value)
                                  )
                                }
                                error={fieldState.invalid}
                                type="text"
                                autoFocus={true}
                                tabIndex={1}
                                inputRef={inputRef[0]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 0)
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    border: "none !important",
                                    borderRadius: "10px !important",
                                  },
                                }}
                                placeholder="First"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={4} sx={{ pr: "0.57rem" }}>
                          <Controller
                            name="middleName"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeFirst(e.target.value)
                                  )
                                }
                                error={fieldState.invalid}
                                type="text"
                                tabIndex={2}
                                inputRef={inputRef[1]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 1)
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    border: "none !important",
                                    borderRadius: "10px !important",
                                  },
                                }}
                                placeholder="Middle"
                              />
                            )}
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <Controller
                            name="lastName"
                            control={control}
                            // rules={{
                            //   required: {
                            //     message: "Last Name is required",
                            //     value: true,
                            //   },
                            // }}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                value={field.value ?? ""}
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeFirst(e.target.value)
                                  )
                                }
                                error={fieldState.invalid}
                                type="text"
                                tabIndex={2}
                                inputRef={inputRef[2]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 2)
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    border: "none !important",
                                    borderRadius: "10px !important",
                                  },
                                }}
                                placeholder="Last"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                    {/* Name Inputs */}

                    <Grid
                      container
                      sx={{ mt: "0.67rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle
                          variation="field_label"
                          title="Cell Phone"
                        />
                      </Grid>
                      <Grid
                        item
                        xs={10}
                        sx={{
                          "& .react-international-phone-country-selector-button":
                          {
                            display: "none !important",
                          },
                          "& .react-international-phone-input input": {
                            paddingLeft: "0.5rem !important",
                          },
                        }}
                      >
                        <Controller
                          name="mobilePhone"
                          control={control}
                          rules={{
                            required: true,
                            validate: (value) =>
                              value?.replace(/\D/g, "").length >= 11 ||
                              "Invalid phone number",
                          }}
                          render={({ field }) => (
                            <PhoneInput
                              {...field}
                              disableDialCodeAndPrefix={true}
                              defaultCountry="us"
                              countries={phoneCountries}
                              inputStyle={{
                                width: "100%",
                                height: "2.714rem",
                                fontSize: "1.2rem",
                                textAlign: "left",
                                border:
                                  isPhoneInvalid == true &&
                                    isPhoneInvalid != undefined
                                    ? `0.05rem solid ${Colors.ERROR_RED}`
                                    : `0.05rem solid ${Colors.WHITE}`,
                                borderRadius: "0.2857rem",
                                backgroundColor: Colors.WHITE,
                              }}
                              style={{
                                height: "2.857rem",
                                backgroundColor: Colors.WHITE,
                                borderRadius: "0.2857rem",
                              }}
                              inputProps={{
                                tabIndex: 4,
                                onKeyDown: (event) =>
                                  handleTabAndEnter(inputRef, event, 3),
                              }}
                              inputRef={inputRef[3]}
                              placeholder="+1 XXX XXX XXXX"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      sx={{ mt: "0.67rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle
                          variation="field_label"
                          title="Home Phone"
                        />
                      </Grid>

                      <Grid
                        item
                        xs={10}
                        sx={{
                          "& .react-international-phone-country-selector-button":
                          {
                            display: "none !important",
                          },
                          "& .react-international-phone-input input": {
                            paddingLeft: "0.5rem !important",
                          },
                          "& .react-international-phone-input-container": {
                            borderRadius: "0.714rem",
                          },
                        }}
                      >
                        <Controller
                          name="landPhone"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!value || value.trim() === "") return true;
                              const digits = value.replace(/\D/g, "");
                              return (
                                digits.length === 11 ||
                                "Phone number must be 11 digits"
                              );
                            },
                          }}
                          render={({ field }) => (
                            <PhoneInput
                              {...field}
                              defaultCountry="us"
                              disableDialCodeAndPrefix={true}
                              countries={phoneCountries}
                              onChange={(val) => {
                                const digits = (val ?? "").replace(/\D/g, "");

                                field.onChange(digits.length <= 1 ? "" : val);
                              }}
                              inputProps={{
                                tabIndex: 5,
                                onKeyDown: (event) =>
                                  handleTabAndEnter(inputRef, event, 4),
                              }}
                              inputRef={inputRef[4]}
                              placeholder="+1 XXX XXX XXXX"
                              inputStyle={{
                                width: "100%",
                                height: "2.714rem",
                                fontSize: "1.2rem",
                                textAlign: "left",
                                border:
                                  isLandPhoneInvalid == true &&
                                    isLandPhoneInvalid != undefined
                                    ? `0.05rem solid ${Colors.ERROR_RED}`
                                    : `0.05rem solid ${Colors.WHITE}`,
                                borderRadius: "0.2857rem",
                                backgroundColor: Colors.WHITE,
                              }}
                              style={{
                                height: "2.857rem",
                                backgroundColor: Colors.WHITE,
                                borderRadius: "0.714rem",
                              }}
                            />
                          )}
                        />
                        {isSameContactNumber && (
                          <Typography
                            sx={{
                              color: Colors.ERROR_RED,
                              fontSize: "var(--fs-2xs)",
                              mt: "0.3rem",
                            }}
                          >
                            Home phone cannot be the same as cell phone
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      sx={{ mt: "0.67rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="Email" />
                      </Grid>
                      <Grid item xs={10}>
                        <Controller
                          name="email"
                          control={control}
                          render={({ field, fieldState }) => (
                            <CustomTextBox
                              {...field}
                              error={isEmailInvalid}
                              type="text"
                              tabIndex={6}
                              inputRef={(el) => (inputRef[5].current = el)}
                              onKeyDown={(event) =>
                                handleTabAndEnter(inputRef, event, 5)
                              }
                              onChange={(e) => {
                                const lowercaseValue =
                                  e.target.value.toLowerCase();
                                field.onChange(lowercaseValue);

                                const isValid = validateEmail(lowercaseValue);
                                setIsEmailInvalid(
                                  lowercaseValue.length > 0 && !isValid
                                );
                              }}
                              sx={{
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiInputBase-input": {
                                  textAlign: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    border: isEmailInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                    //borderRadius: "10px !important",
                                  },
                                  "&:hover fieldset": {
                                    border: isEmailInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                  },
                                  "&.Mui-focused fieldset": {
                                    border: isEmailInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                  },
                                },
                              }}
                              placeholder="johndoe@example.com"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      sx={{ mt: "0.67rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="DOB" />
                      </Grid>
                      <Grid item xs={10}>
                        <Controller
                          name="dob"
                          control={control}
                          rules={{
                            required: isAgeRelatedDiscountSelected
                              ? "Date of Birth is required for age-related discount"
                              : false,
                            validate: (value) => {
                              if (!value && !isAgeRelatedDiscountSelected)
                                return true;

                              if (isAgeRelatedDiscountSelected && !value) {
                                return "Date of Birth is required for age-related discount";
                              }

                              if (value) {
                                const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/;
                                if (!dateRegex.test(value))
                                  return "Invalid date format";

                                const [month, day, year] = value
                                  .split("/")
                                  .map(Number);

                                if (month < 1 || month > 12)
                                  return "Month must be between 1 and 12";
                                if (day < 1 || day > 31)
                                  return "Day must be between 1 and 31";

                                const enteredDate = new Date(
                                  year,
                                  month - 1,
                                  day
                                );
                                if (
                                  enteredDate.getFullYear() !== year ||
                                  enteredDate.getMonth() !== month - 1 ||
                                  enteredDate.getDate() !== day
                                ) {
                                  return "Invalid calendar date";
                                }

                                const today = new Date();
                                const currentYear = today.getFullYear();
                                const hundredYearsAgo = new Date();
                                hundredYearsAgo.setFullYear(currentYear - 100);

                                if (enteredDate < hundredYearsAgo) {
                                  return `DOB cannot be before ${currentYear - 100
                                    }`;
                                }

                                if (
                                  enteredDate.getTime() >=
                                  new Date().setHours(0, 0, 0, 0)
                                ) {
                                  return "DOB cannot be today or a future date";
                                }
                              }

                              return true;
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <CustomTextBox
                              {...field}
                              error={isDobInvalid}
                              helperText={fieldState.error?.message || ""}
                              type="text"
                              tabIndex={7}
                              inputRef={(el) => (inputRef[6].current = el)}
                              // onKeyDown={(event) =>
                              //   handleTabAndEnter(inputRef, event, 6)
                              // }

                              onKeyDown={(
                                event: React.KeyboardEvent<HTMLInputElement>
                              ) => {
                                handleTabAndEnter(inputRef, event, 6);

                                if (event.key === "Backspace") {
                                  const target = event.currentTarget;
                                  const currentValue = field.value || "";
                                  const cursorPosition = target.selectionStart;

                                  if (
                                    cursorPosition !== null &&
                                    cursorPosition > 0 &&
                                    currentValue[cursorPosition - 1] === "/"
                                  ) {
                                    event.preventDefault();
                                    const newValue =
                                      currentValue.slice(
                                        0,
                                        cursorPosition - 2
                                      ) + currentValue.slice(cursorPosition);
                                    field.onChange(newValue);

                                    setTimeout(() => {
                                      target.setSelectionRange(
                                        cursorPosition - 2,
                                        cursorPosition - 2
                                      );
                                    }, 0);
                                  }
                                }
                              }}
                              onClick={(e) => {
                                const target = e.currentTarget as HTMLInputElement;
                                const cursorPosition = target.selectionStart;
                                const currentValue = field.value || "";

                                if (cursorPosition !== null && currentValue) {
                                  if (cursorPosition <= 2) {
                                    // Select Month
                                    target.setSelectionRange(0, 2);
                                  } else if (cursorPosition > 2 && cursorPosition <= 5) {
                                    // Select Day
                                    target.setSelectionRange(3, 5);
                                  } else if (cursorPosition > 5) {
                                    // Select Year
                                    target.setSelectionRange(6, 10);
                                  }
                                }
                              }}
                              onChange={(e) => {
                                const currentValue = field.value || "";
                                const newValue = e.target.value;

                                const isDeleting =
                                  newValue.length < currentValue.length;

                                if (isDeleting) {
                                  field.onChange(newValue);

                                  const isValid = validateDobFormat(newValue);
                                  //setIsDobInvalid(newValue.length > 0 && !isValid);
                                  setIsDobInvalid(
                                    (isAgeRelatedDiscountSelected &&
                                      (newValue.length === 0 || !isValid)) ||
                                    (newValue.length > 0 && !isValid)
                                  );
                                  return;
                                }

                                let value = newValue;

                                if (value.endsWith('/')) {
                                  const partsArray = value.slice(0, -1).split('/');
                                  if (partsArray.length === 1 && partsArray[0].length === 1) {
                                    value = `0${partsArray[0]}/`;
                                  } else if (partsArray.length === 2 && partsArray[1].length === 1) {
                                    value = `${partsArray[0]}/0${partsArray[1]}/`;
                                  }
                                }

                                let digits = value.replace(/\D/g, "");

                                if (digits.length === 1 && parseInt(digits, 10) > 1) {
                                  digits = "0" + digits;
                                }

                                if (digits.length === 3) {
                                  const lastDigit = parseInt(digits.slice(2), 10);
                                  if (lastDigit > 3) {
                                    digits = digits.slice(0, 2) + "0" + digits.slice(2);
                                  }
                                }

                                let formatted = digits;
                                if (formatted.length >= 2) {
                                  formatted = formatted.slice(0, 2) + "/" + formatted.slice(2);
                                }
                                if (formatted.length >= 5) {
                                  formatted = formatted.slice(0, 5) + "/" + formatted.slice(5);
                                }
                                if (formatted.length > 10) {
                                  formatted = formatted.slice(0, 10);
                                }

                                const parts = formatted.split("/");

                                if (parts[0]) {
                                  const month = parseInt(parts[0], 10);
                                  if (month > 12) return;
                                }

                                if (parts[1]) {
                                  const day = parseInt(parts[1], 10);
                                  if (day > 31) return;
                                }

                                if (
                                  parts.length === 3 &&
                                  parts[2]?.length === 4
                                ) {
                                  const year = parseInt(parts[2], 10);
                                  const currentYear = new Date().getFullYear();
                                  if (year < currentYear - 100) return;
                                  if (year > currentYear) return;
                                }

                                field.onChange(formatted);

                                const isValid = validateDobFormat(formatted);
                                //setIsDobInvalid(formatted.length > 0 && !isValid);
                                setIsDobInvalid(
                                  (isAgeRelatedDiscountSelected &&
                                    (formatted.length === 0 || !isValid)) ||
                                  (formatted.length > 0 && !isValid)
                                );
                              }}
                              sx={{
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiInputBase-input": {
                                  textAlign: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  "& fieldset": {
                                    border: isDobInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                    //borderRadius: "10px !important",
                                  },
                                  "&:hover fieldset": {
                                    border: isDobInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                  },
                                  "&.Mui-focused fieldset": {
                                    border: isDobInvalid
                                      ? `0.05rem solid ${Colors.ERROR_RED} !important`
                                      : "none !important",
                                  },
                                },
                              }}
                              placeholder="MM/DD/YYYY"
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </PanelLayout>
                </Grid>
                {/* Personal Information */}

                {/* Miscellaneous */}
                <Grid
                  item
                  xs={6}
                  sx={{ padding: "0.57rem", maxHeight: "27.7rem" }}
                >
                  <PanelLayout title="Preferences">
                    <Grid
                      container
                      sx={{ mt: "1rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="Starch" />
                      </Grid>
                      <Grid item xs={10}>
                        <MultiTabSelector
                          fieldName="preferenceStarch"
                          data-testid="preferenceStarch"
                          items={[
                            {
                              value: "NONE",
                              label: "None",
                              tabIndex: 14,
                              ref: inputRef[13],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 13),
                            },
                            {
                              value: "LIGHT",
                              label: "Light",
                              tabIndex: 15,
                              ref: inputRef[14],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 14),
                            },
                            {
                              value: "MEDIUM",
                              label: "Medium",
                              tabIndex: 16,
                              ref: inputRef[15],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 15),
                            },
                            {
                              value: "HEAVY",
                              label: "Heavy",
                              tabIndex: 17,
                              ref: inputRef[16],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 16),
                            },
                          ]}
                          value={getValues("preferenceStarch") as Array<string>}
                          onChange={(value) => {
                            setValue("preferenceStarch", value, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          layout="horizontal"
                          mode="single"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      sx={{ mt: "0.57rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="Shirt" />
                      </Grid>
                      <Grid item xs={10}>
                        <MultiTabSelector
                          fieldName="preferenceShirt"
                          data-testid="preferenceShirt"
                          items={[
                            {
                              value: "HANGER",
                              label: "Hanger",
                              tabIndex: 18,
                              ref: inputRef[17],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 17),
                            },
                            {
                              value: "BOXED",
                              label: "Boxed",
                              tabIndex: 19,
                              ref: inputRef[18],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 18),
                            },
                          ]}
                          value={getValues("preferenceShirt") as Array<string>}
                          onChange={(value) => {
                            setValue("preferenceShirt", value, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          layout="horizontal"
                          mode="single"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      sx={{ mt: "0.57rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="Pickup" />
                      </Grid>
                      <Grid item xs={10}>
                        <MultiTabSelector
                          fieldName="pickupPref"
                          data-testid="pickupPref"
                          items={[
                            {
                              value: "REGULAR",
                              label: "Regular",
                              tabIndex: 20,
                              ref: inputRef[19],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 19),
                            },
                            {
                              value: "SPECIAL",
                              label: "Special",
                              tabIndex: 21,
                              ref: inputRef[20],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 20),
                            },
                          ]}
                          value={getValues("pickupPref") as Array<string>}
                          onChange={(value) => {
                            setValue("pickupPref", value, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          layout="horizontal"
                          mode="single"
                        />
                      </Grid>
                    </Grid>
                    <Grid
                      container
                      sx={{ mt: "0.57rem", alignItems: "center" }}
                    >
                      <Grid item xs={2}>
                        <HeaderTitle variation="field_label" title="Pants" />
                      </Grid>
                      <Grid item xs={10}>
                        <MultiTabSelector
                          fieldName="pantsPref"
                          data-testid="pantsPref"
                          items={[
                            {
                              value: "CREASE",
                              label: "Crease",
                              tabIndex: 22,
                              ref: inputRef[21],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 21),
                            },
                            {
                              value: "NO_CREASE",
                              label: "No Crease",
                              tabIndex: 23,
                              ref: inputRef[22],
                              onKeyDown: (event) =>
                                handleTabAndEnter(inputRef, event, 22),
                            },
                          ]}
                          value={getValues("pantsPref") as Array<string>}
                          onChange={(value) => {
                            setValue("pantsPref", value, {
                              shouldDirty: true,
                              shouldTouch: true,
                            });
                          }}
                          layout="horizontal"
                          mode="single"
                        />
                      </Grid>
                    </Grid>
                  </PanelLayout>
                </Grid>
                {/* Miscellaneous */}

                {/* Address */}
                <>
                  {/* Personal Address */}
                  <Grid
                    item
                    xs={6}
                    sx={{ padding: "0.57rem", minHeight: "27.6rem" }}
                  >
                    <PanelLayout
                      title="Personal Address"
                      headerRight={
                        <Box sx={{ mt: 0.8 }}>
                          <Controller
                            name="notWillingToProvide"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                              <CustomCheckBox
                                {...field}
                                label="Not Willing To Provide"
                                checked={!!field.value}
                                disabled={
                                  !firstName ||
                                  !lastName ||
                                  mobilenum == "+1" ||
                                  mobilenum.length != 12
                                }
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  field.onChange(checked);

                                  if (checked) {
                                    setIsSignatureModalOpen(true);
                                  } else {
                                    setCapturedSignature(null);
                                    handleNotWillingToProvideChange(false);
                                    trigger("notWillingToProvide");
                                  }
                                }}
                                tabIndex={8}
                                ref={inputRef[7] as any}
                                inputProps={{
                                  tabIndex: 8,
                                  onKeyDown: (event) => {
                                    if (
                                      event.key === "Tab" ||
                                      event.key === "Enter"
                                    ) {
                                      event.preventDefault();

                                      const isNotWilling = getValues(
                                        "notWillingToProvide"
                                      );

                                      if (isNotWilling) {
                                        const starchElement = inputRef[13]
                                          ?.current as any;
                                        if (
                                          starchElement &&
                                          typeof starchElement.focus ===
                                          "function"
                                        ) {
                                          starchElement.focus();
                                        }
                                      } else {
                                        const streetElement = inputRef[8]
                                          ?.current as any;
                                        if (
                                          streetElement &&
                                          typeof streetElement.focus ===
                                          "function"
                                        ) {
                                          streetElement.focus();
                                        }
                                      }
                                    }
                                  },
                                }}
                              />
                            )}
                          />
                        </Box>
                      }
                    >
                      {notWillingToProvide ? (
                        <Box
                          sx={{
                            mt: "2.1rem",
                            backgroundColor: Colors.WHITE,
                            borderRadius: "8px",
                            padding: "1.5rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "12rem",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: Colors.MAIN_GRAY,
                              fontSize: "1.1rem",
                              textAlign: "center",
                              mb: 3,
                            }}
                          >
                            No address provided. Address Waiver received.
                          </Typography>
                          <CustomButton
                            variant="contained"
                            sx={{
                              backgroundColor: Colors.SECONDARY,
                              color: Colors.WHITE,
                              borderRadius: "5px",
                              px: 4,
                              py: 1,
                              fontSize: "1.1rem",
                              "&:hover": {
                                backgroundColor: Colors.SECONDARY,
                              },
                            }}
                            onClick={() => setIsWaiverViewModalOpen(true)}
                          >
                            View
                          </CustomButton>
                        </Box>
                      ) : (
                        <>
                      {/* Personal Street */}
                      <Grid
                        container
                        sx={{
                          mt: "1rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="Street" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="personalAddressLine1"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                error={fieldState.invalid}
                                type="text"
                                disabled={notWillingToProvide}
                                tabIndex={9}
                                inputRef={inputRef[8]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 8)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="Street"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Personal Apt/Unit */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle
                            variation="field_label"
                            title=" Apt/Unit"
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="personalAddressLine2"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                error={fieldState.invalid}
                                type="text"
                                disabled={notWillingToProvide}
                                tabIndex={10}
                                inputRef={inputRef[9]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 9)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="Apt/Unit"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Personal City */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="City" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="personalCity"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                error={fieldState.invalid}
                                type="text"
                                disabled={notWillingToProvide}
                                tabIndex={11}
                                inputRef={inputRef[10]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 10)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="City"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Personal State */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="State" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="personalState"
                            control={control}
                            render={({ field, fieldState }) => (
                              <CustomSelectBox
                                value={field.value ?? states.ALABAMA}
                                onChange={(e) =>
                                  field.onChange(
                                    (e.target as HTMLInputElement).value
                                  )
                                }
                                onBlur={field.onBlur}
                                error={!!fieldState.error}
                                formControlProps={{
                                  fullWidth: true,
                                  sx: {
                                    width: "100%",
                                    backgroundColor: Colors.WHITE,
                                    "& .MuiSelect-select": {
                                      textAlign: "left",
                                      display: "flex",
                                      justifyContent: "left",
                                      alignItems: "left",
                                      fontSize: "1.04rem",
                                    },
                                    "& .MuiOutlinedInput-root": {
                                      borderRadius: "0.157rem",
                                      pl: 0.5,
                                    },
                                    "& fieldset": {
                                      border: "none",
                                    },
                                  },
                                }}
                                ref={inputRef[11]}
                                inputProps={{
                                  tabIndex: 12,
                                  onBlur: (event) =>
                                    handleTabAndEnter(inputRef, event, 11),
                                  disabled: notWillingToProvide,
                                }}
                                disabled={notWillingToProvide}
                              >
                                {renderStateMenuItems()}
                              </CustomSelectBox>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Personal Zip */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle
                            variation="field_label"
                            title="Zip Code"
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="personalZip"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                error={fieldState.invalid}
                                type="number"
                                disabled={notWillingToProvide}
                                tabIndex={13}
                                inputRef={inputRef[12]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 12)
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& input::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                                  {
                                    "-webkit-appearance": "none",
                                    margin: 0,
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                  "& inputMozAppearance": "textfield",
                                }}
                                placeholder="Zip"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                        </>
                      )}
                    </PanelLayout>
                  </Grid>
                  {/* Personal Address */}

                  {/* Billing Address */}
                  <Grid
                    item
                    xs={6}
                    sx={{ padding: "0.57rem", minHeight: "27rem" }}
                  >
                    <PanelLayout
                      title="Billing Address"
                      headerRight={
                        <Box sx={{ mt: 0.8 }}>
                          <Controller
                            name="billingSameAsPersonalAddress"
                            control={control}
                            render={({ field }) => (
                              <CustomCheckBox
                                {...field}
                                label="Same as personal address"
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  field.onChange(e);
                                  const originalValue =
                                    (customer as any)
                                      ?.billingSameAsPersonalAddress ?? false;
                                  const isDirtyNow =
                                    isChecked !== originalValue;
                                  setValue(
                                    "billingSameAsPersonalAddress",
                                    isChecked,
                                    {
                                      shouldDirty: isDirtyNow,
                                    }
                                  );
                                  if (!isDirtyNow) {
                                    reset(getValues());
                                  }
                                  trigger("billingSameAsPersonalAddress");
                                }}
                                tabIndex={24}
                                inputRef={inputRef[23]}
                                inputProps={{
                                  tabIndex: 24,
                                  onKeyDown: (event) => {
                                    handleTabAndEnter(inputRef, event, 23);
                                  },
                                  disabled:
                                    watch("notWillingToProvide") ||
                                    !firstName ||
                                    !lastName,
                                }}
                                disabled={watch("notWillingToProvide")}
                                checked={field.value}
                              />
                            )}
                          />
                        </Box>
                      }
                    >
                      {notWillingToProvide ? (
                        <Box
                          sx={{
                            mt: "2.1rem",
                            backgroundColor: Colors.WHITE,
                            borderRadius: "8px",
                            padding: "1.5rem",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "12rem",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: "bold",
                              color: Colors.MAIN_GRAY,
                              fontSize: "1.1rem",
                              textAlign: "center",
                              mb: 3,
                            }}
                          >
                            No address provided. Address Waiver received.
                          </Typography>
                          <CustomButton
                            variant="contained"
                            sx={{
                              backgroundColor: Colors.SECONDARY,
                              color: Colors.WHITE,
                              borderRadius: "5px",
                              px: 4,
                              py: 1,
                              fontSize: "1.1rem",
                              "&:hover": {
                                backgroundColor: Colors.SECONDARY,
                              },
                            }}
                            onClick={() => setIsWaiverViewModalOpen(true)}
                          >
                            View
                          </CustomButton>
                        </Box>
                      ) : (
                        <>
                      {/* billing Street */}
                      <Grid
                        container
                        sx={{
                          mt: "1rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="Street" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="billingAddressLine1"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                disabled={
                                  watch("notWillingToProvide") ||
                                  watch("billingSameAsPersonalAddress")
                                }
                                error={fieldState.invalid}
                                type="text"
                                tabIndex={25}
                                inputRef={inputRef[24]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 24)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="Street"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* billing Apt/Unit */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle
                            variation="field_label"
                            title=" Apt/Unit"
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="billingAddressLine2"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                disabled={
                                  watch("notWillingToProvide") ||
                                  watch("billingSameAsPersonalAddress")
                                }
                                error={fieldState.invalid}
                                type="text"
                                tabIndex={26}
                                inputRef={inputRef[25]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 25)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="Apt/Unit"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* billing City */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="City" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="billingCity"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                disabled={
                                  watch("notWillingToProvide") ||
                                  watch("billingSameAsPersonalAddress")
                                }
                                error={fieldState.invalid}
                                type="text"
                                tabIndex={27}
                                inputRef={inputRef[26]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 26)
                                }
                                onChange={(e) =>
                                  field.onChange(
                                    capitalizeEachWord(e.target.value)
                                  )
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                }}
                                placeholder="City"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* billing State */}
                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle variation="field_label" title="State" />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="billingState"
                            control={control}
                            render={({ field, fieldState }) => {
                              const disabled =
                                watch("notWillingToProvide") ||
                                watch("billingSameAsPersonalAddress");
                              return (
                                <CustomSelectBox
                                  value={field.value ?? states.ALABAMA}
                                  onChange={(e) =>
                                    field.onChange(
                                      (e.target as HTMLInputElement).value
                                    )
                                  }
                                  onBlur={field.onBlur}
                                  error={!!fieldState.error}
                                  formControlProps={{
                                    fullWidth: true,
                                    sx: {
                                      width: "100%",
                                      backgroundColor: Colors.WHITE,
                                      "& .MuiSelect-select": {
                                        textAlign: "left",
                                        display: "flex",
                                        justifyContent: "left",
                                        alignItems: "left",
                                        fontSize: "1.2rem",
                                        padding: "0.3rem 0.5rem",
                                      },
                                      "& .MuiOutlinedInput-root": {
                                        borderRadius: "0.157rem",
                                        pl: 0.5,
                                      },
                                      "& fieldset": {
                                        border: "none",
                                      },
                                    },
                                  }}
                                  ref={inputRef[27]}
                                  inputProps={{
                                    tabIndex: 28,
                                    onBlur: (event) =>
                                      handleTabAndEnter(inputRef, event, 27),
                                    disabled,
                                  }}
                                  disabled={disabled}
                                >
                                  {renderStateMenuItems()}
                                </CustomSelectBox>
                              );
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        sx={{
                          mt: "0.67rem",
                          height: "2.85rem",
                          alignItems: "center",
                        }}
                      >
                        <Grid item xs={2}>
                          <HeaderTitle
                            variation="field_label"
                            title="Zip Code"
                          />
                        </Grid>
                        <Grid item xs={10}>
                          <Controller
                            name="billingZip"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomTextBox
                                {...field}
                                error={fieldState.invalid}
                                type="number"
                                disabled={
                                  watch("notWillingToProvide") ||
                                  watch("billingSameAsPersonalAddress")
                                }
                                tabIndex={29}
                                inputRef={inputRef[28]}
                                onKeyDown={(event) =>
                                  handleTabAndEnter(inputRef, event, 28)
                                }
                                sx={{
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiInputBase-input": {
                                    textAlign: "left",
                                  },
                                  "& input::-webkit-inner-spin-button, &::-webkit-outer-spin-button":
                                  {
                                    "-webkit-appearance": "none",
                                    margin: 0,
                                  },
                                  "& fieldset": {
                                    borderRadius: "10px",
                                    border: "none",
                                  },
                                  "& inputMozAppearance": "textfield",
                                }}
                                placeholder="Zip"
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                        </>
                      )}
                    </PanelLayout>
                  </Grid>

                  {/* Billing Address */}
                </>
                {/* Address */}
              </Grid>
              <Grid
                container
                sx={{
                  height: "14.13rem",
                }}
              ></Grid>

              {/* Card Information */}
              <Grid
                container
                spacing={0.5}
                sx={{
                  padding: "0.57rem",
                  marginTop: "-14.4rem",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              >
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[44]}
                        tabIndex={45}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 44)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: "pointer",
                        }}
                        onClick={handleOpenCardInformationModal}
                      >
                        <svg
                          width="29"
                          height="28"
                          viewBox="0 0 33 32"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M16.2961 2.66406C23.6568 2.66406 29.6234 8.63071 29.6234 15.9914C29.6234 23.3521 23.6568 29.3187 16.2961 29.3187C8.9354 29.3187 2.96875 23.3521 2.96875 15.9914C2.96875 8.63071 8.9354 2.66406 16.2961 2.66406ZM16.2828 13.3259H14.9634C14.6237 13.3263 14.2969 13.4564 14.0499 13.6896C13.8029 13.9228 13.6543 14.2415 13.6344 14.5806C13.6145 14.9197 13.7248 15.2536 13.9428 15.5141C14.1609 15.7745 14.4701 15.942 14.8074 15.9821L14.9634 15.9914V22.6417C14.9634 23.3348 15.4885 23.9078 16.1628 23.9798L16.3094 23.9878H16.9625C17.2427 23.9878 17.5159 23.8994 17.7431 23.7353C17.9703 23.5711 18.1399 23.3395 18.2279 23.0734C18.3158 22.8072 18.3177 22.5201 18.2331 22.2529C18.1485 21.9857 17.9818 21.7519 17.7568 21.5849L17.6288 21.5009V14.672C17.6288 13.979 17.1037 13.4059 16.4294 13.3339L16.2828 13.3259ZM16.2961 9.32773C15.9426 9.32773 15.6036 9.46814 15.3537 9.71808C15.1038 9.96802 14.9634 10.307 14.9634 10.6605C14.9634 11.0139 15.1038 11.3529 15.3537 11.6028C15.6036 11.8528 15.9426 11.9932 16.2961 11.9932C16.6496 11.9932 16.9885 11.8528 17.2385 11.6028C17.4884 11.3529 17.6288 11.0139 17.6288 10.6605C17.6288 10.307 17.4884 9.96802 17.2385 9.71808C16.9885 9.46814 16.6496 9.32773 16.2961 9.32773Z"
                            fill="#0079C2"
                          />
                        </svg>

                        <span
                          style={{
                            marginTop: "1rem",
                            color: Colors.SECONDARY,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                            justifyContent: "center",
                          }}
                        >
                          Card Information
                        </span>
                      </CustomButton>
                    </Box>
                  </Panel>
                </Grid>
                <CardInformationModal
                  open={isCardInformationModalOpen}
                  onClose={handleCloseCardInformationModal}
                />

                {/* Update Cards */}
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[45]}
                        tabIndex={46}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 45)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: "pointer",
                        }}
                        onClick={handleOpenUpdateCardsModal}
                      >
                        <BrowserUpdatedIcon
                          sx={{ fontSize: "3rem", color: Colors.SECONDARY }}
                        />
                        <span
                          style={{
                            marginTop: "1rem",
                            color: Colors.SECONDARY,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                            justifyContent: "center",
                          }}
                        >
                          Update Cards
                        </span>
                      </CustomButton>
                    </Box>
                  </Panel>
                </Grid>
                <UpdateCardsModal
                  open={isUpdateCardsModalOpen}
                  onClose={handleCloseUpdateCardsModal}
                />

                <AddressWaiverViewModal
                  open={isWaiverViewModalOpen}
                  onClose={() => setIsWaiverViewModalOpen(false)}
                  customerName={
                    capturedSignature?.customerName ||
                    `${getValues("firstName") || ""} ${getValues("lastName") || ""}`.trim() ||
                    "Customer"
                  }
                  customerPhone={`${getValues("mobilePhone") || ""}`.trim()}
                  signatureDataUrl={capturedSignature?.imageBase64 ?? existingSignature?.signatureImage}
                />

                <SignatureCaptureModal
                  open={isSignatureModalOpen}
                  onClose={handleSignatureModalClose}
                  onCapture={handleSignatureCaptured}
                  agreementText={agreementText}
                  onDisagree={() => setIsDisagreeModalOpen(true)}
                />

                <WarningDialog
                  open={isDisagreeModalOpen}
                  onClose={() => {
                    setIsDisagreeModalOpen(false);
                    setValue("notWillingToProvide", false);
                    trigger("notWillingToProvide");
                  }}
                  onOk={() => {
                    setIsDisagreeModalOpen(false);
                    setValue("notWillingToProvide", false);
                    trigger("notWillingToProvide");
                  }}
                  message={"The customer has declined the agreement.\n\nThe address waiver has not been accepted. Please proceed with providing the address, or speak with the customer."}
                />

                {/* Store Credits */}
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[46]}
                        tabIndex={47}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 46)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: "pointer",
                        }}
                        disabled={mode === "create"}
                        onClick={() => setOpenStoreCreditsModal(true)}
                      >
                        <PaidIcon
                          sx={{
                            fontSize: "3rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                          }}
                        />
                        <Typography
                          sx={{
                            marginTop: "1rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                          }}
                        >
                          Store Credits
                        </Typography>
                      </CustomButton>
                    </Box>
                  </Panel>
                  <StoreCreditsModal
                    open={openStoreCreditsModal}
                    onClose={handleOnCloseStoreCreditsModal}
                    isCustomerEdited={isDirtyState}
                    resetDirtyState={changeFormDirtyState}
                  />
                </Grid>

                {/* Print Barcode */}
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[47]}
                        tabIndex={48}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 47)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: "pointer",
                        }}
                        disabled={mode === "create"}
                        onClick={handleOpenPrintModal}
                      >
                        <QrCodeIcon
                          sx={{
                            fontSize: "3rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                          }}
                        />
                        <span
                          style={{
                            marginTop: "1rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                          }}
                        >
                          Customer Tag
                        </span>
                      </CustomButton>
                    </Box>
                  </Panel>
                </Grid>

                <PrintCustomerTagModal
                  open={isPrintModalOpen}
                  onClose={handleClosePrintModal}
                />

                {/* Tax Excemption */}
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[48]}
                        tabIndex={49}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 48)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: taxEnabled && mode === "edit" ? "pointer" : "not-allowed",
                          opacity: taxEnabled && mode === "edit" ? 1 : 0.4,
                        }}
                        onClick={taxEnabled && mode === "edit" ? handleOpenTaxModal : undefined}
                        disabled={!taxEnabled || mode === "create"}
                      >
                        <ReceiptIcon
                          sx={{
                            fontSize: "3rem",
                            color: taxEnabled && mode === "edit" ? Colors.SECONDARY : Colors.TEXT_GRAY_DARK,
                          }}
                        />
                        <span
                          style={{
                            marginTop: "1rem",
                            color: taxEnabled && mode === "edit" ? Colors.SECONDARY : Colors.TEXT_GRAY_DARK,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                          }}
                        >
                          Tax Exemption
                        </span>
                      </CustomButton>
                    </Box>
                  </Panel>
                </Grid>

                <TaxExceptionModal
                  open={isTaxModalOpen}
                  onClose={handleCloseTaxModal}
                  customerId={Number(customerId)}
                />

                {/* Manage Notes */}
                <Grid item xs={2.0}>
                  <Panel>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        width: "100%",
                      }}
                    >
                      <CustomButton
                        ref={inputRef[49]}
                        tabIndex={50}
                        onKeyDown={(event) =>
                          handleTabAndEnter(inputRef, event, 49)
                        }
                        sx={{
                          background: "none",
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexDirection: "column",
                          height: "10rem",
                          cursor: "pointer",
                        }}
                        disabled={mode === "create"}
                        onClick={() => setOpenNotesModal(true)}
                      >
                        <TextSnippetIcon
                          sx={{
                            fontSize: "3rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                          }}
                        />
                        <Typography
                          sx={{
                            marginTop: "1rem",
                            color:
                              mode === "create"
                                ? Colors.ACCENT_GRAY
                                : Colors.SECONDARY,
                            fontWeight: "bold",
                            fontSize: "1.2rem",
                          }}
                        >
                          Manage Notes
                        </Typography>
                      </CustomButton>
                    </Box>
                  </Panel>
                  {openNotesModal && ( //Since this is called to fetch the user note, this conditional rendering is used to prevent it.
                    <UserNotesModal
                      open={openNotesModal}
                      onClose={handleOnNotesModalClose}
                    />
                  )}
                </Grid>
              </Grid>
              {/* Notes */}
            </Grid>
            <Grid item xs={4} sx={{ display: "flex", flexDirection: "column" }}>
              <Grid sx={{ padding: "0.57rem", flex: 1, display: "flex", flexDirection: "column" }}>
                {/* Preferences */}
                <PanelLayout title="Additionals">
                  <Grid container sx={{ mt: "1rem", alignItems: "center" }}>
                    <Grid item xs={3}>
                      <HeaderTitle
                        variation="field_label"
                        title=" Price List"
                      />
                    </Grid>
                    <Grid item xs={9} sx={{ height: "2.85rem" }}>
                      <Controller
                        name="priceList"
                        control={control}
                        // rules={{
                        //   required: {
                        //     message: "Price List is required",
                        //     value: true,
                        //   },
                        // }}
                        render={({ field, fieldState }) => (
                          <CustomSelectBox
                            {...field}
                            error={Boolean(fieldState.error)}
                            formControlProps={{
                              fullWidth: true,
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  justifyContent: "left",
                                  textAlign: "left",
                                },
                                "& .MuiInputBase-root": {
                                  justifyContent: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.157rem",
                                },
                                "& fieldset": {
                                  borderRadius: "10px",
                                  border: "none",
                                },
                              },
                            }}
                            defaultValue="1"
                            tabIndex={30}
                            ref={inputRef[29]}
                            inputProps={{
                              tabIndex: 30,
                              onBlur: (event) => {
                                handleTabAndEnter(inputRef, event, 29);
                              },
                            }}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              const currentValue = field.value;

                              if (newValue !== currentValue) {
                                field.onChange(newValue);
                              }
                            }}
                          >
                            <MenuItem
                              ref={inputRef[30]}
                              tabIndex={31}
                              value="1"
                            >
                              Default
                            </MenuItem>
                            <MenuItem
                              ref={inputRef[31]}
                              tabIndex={32}
                              value="2"
                            >
                              List 1
                            </MenuItem>
                            <MenuItem
                              ref={inputRef[32]}
                              tabIndex={33}
                              value="3"
                            >
                              List 2
                            </MenuItem>
                            <MenuItem
                              ref={inputRef[33]}
                              tabIndex={34}
                              value="4"
                            >
                              List 3
                            </MenuItem>
                          </CustomSelectBox>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    sx={{
                      mt: "0.67rem",
                      height: "2.85rem",
                      alignItems: "center",
                    }}
                  >
                    <Grid item xs={3}>
                      <HeaderTitle variation="field_label" title=" Discounts" />
                    </Grid>
                    <Grid item xs={9}>
                      <Controller
                        name="discount"
                        control={control}
                        // rules={{
                        //   required: {
                        //     message: "Discount is required",
                        //     value: true,
                        //   },
                        // }}
                        render={({ field, fieldState }) => (
                          <CustomSelectBox
                            {...field}
                            error={Boolean(fieldState.error)}
                            value={field.value || "none"}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                            }}
                            formControlProps={{
                              fullWidth: true,
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  justifyContent: "left",
                                  textAlign: "left",
                                },
                                "& .MuiInputBase-root": {
                                  justifyContent: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.157rem",
                                },
                                "& fieldset": {
                                  borderRadius: "10px",
                                  border: "none",
                                },
                              },
                            }}
                            tabIndex={35}
                            ref={inputRef[34]}
                            inputProps={{
                              tabIndex: 35,
                              onBlur: (event) => {
                                handleTabAndEnter(inputRef, event, 34);
                              },
                            }}
                          >
                            <MenuItem value="none">None</MenuItem>
                            {getDiscountByStoreResponse?.body.map(
                              (discount) => (
                                <MenuItem value={discount.id}>
                                  {discount.discountName}
                                </MenuItem>
                              )
                            )}
                          </CustomSelectBox>
                        )}
                      />
                    </Grid>
                  </Grid>
                  <Grid container sx={{ mt: "1rem" }}>
                    <Grid item xs={3} sx={{ mt: "-0.34rem" }}>
                      <HeaderTitle
                        variation="field_label"
                        title="Email Options"
                      />
                    </Grid>

                    <Grid container item xs={9}>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          ml: "1rem",
                          height: "2rem",
                          display: "flex",
                        }}
                      >
                        <Controller
                          name="subEmailPromotions"
                          control={control}
                          // rules={{}}
                          render={({ field, fieldState }) => (
                            <CustomCheckBox
                              {...field}
                              label="Promotions"
                              // tabIndex={15}
                              // ref={inputRef[14]}
                              // onKeyDown={(event) =>
                              //   handleTabAndEnter(inputRef, event, 14)
                              // }
                              // inputProps={{
                              //   tabIndex: 15,
                              //   onKeyDown: (event) => {
                              //     handleTabAndEnter(inputRef, event, 14);
                              //   },
                              // }}
                              tabIndex={36}
                              ref={inputRef[35]}
                              inputProps={{
                                tabIndex: 36,
                                onKeyDown: (event) => {
                                  handleTabAndEnter(inputRef, event, 35);
                                },
                              }}
                              checked={getValues("subEmailPromotions")}
                            />
                          )}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={6}
                        sx={{
                          ml: "-1rem",
                          height: "2rem",
                          display: "flex",
                        }}
                      >
                        <Controller
                          name="subEmailMonthlyStatements"
                          control={control}
                          // rules={{}}
                          render={({ field, fieldState }) => (
                            <CustomCheckBox
                              {...field}
                              label="Monthly Statements"
                              // tabIndex={16}
                              tabIndex={37}
                              ref={inputRef[36]}
                              inputProps={{
                                tabIndex: 37,
                                onKeyDown: (event) => {
                                  handleTabAndEnter(inputRef, event, 36);
                                },
                              }}
                              checked={getValues("subEmailMonthlyStatements")}
                            />
                          )}
                        />
                      </Grid>

                      <Grid container sx={{ mt: "0.57rem" }}>
                        <Grid
                          item
                          xs={6}
                          sx={{
                            ml: "1rem",
                            height: "2rem",
                            display: "flex",
                          }}
                        >
                          <Controller
                            name="subEmailInvoices"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomCheckBox
                                {...field}
                                label="Invoices"
                                // tabIndex={17}
                                tabIndex={38}
                                ref={inputRef[37]}
                                inputProps={{
                                  tabIndex: 38,
                                  onKeyDown: (event) => {
                                    handleTabAndEnter(inputRef, event, 37);
                                  },
                                }}
                                checked={getValues("subEmailInvoices")}
                              />
                            )}
                          />
                        </Grid>

                        <Grid
                          item
                          xs={6}
                          sx={{
                            ml: "-1rem",
                            height: "2rem",
                            display: "flex",
                          }}
                        >
                          <Controller
                            name="subMiscellaneous"
                            control={control}
                            // rules={{}}
                            render={({ field, fieldState }) => (
                              <CustomCheckBox
                                {...field}
                                label="Miscellaneous"
                                // tabIndex={18}
                                tabIndex={39}
                                ref={inputRef[38]}
                                inputProps={{
                                  tabIndex: 39,
                                  onKeyDown: (event) => {
                                    handleTabAndEnter(inputRef, event, 38);
                                  },
                                }}
                                checked={getValues("subMiscellaneous")}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      background: Colors.ACCENT_GRAY,
                      width: "90%",
                      height: "0.142rem",
                      margin: "2rem auto",
                      mt: 2,
                      mb: 1.5,
                    }}
                  ></Box>

                  {/* Customer Type */}
                  <Grid container sx={{ mt: "0.57rem", alignItems: "center" }}>
                    <Grid item xs={4}>
                      <HeaderTitle
                        variation="field_label"
                        title="Customer Type"
                      />
                    </Grid>

                    <Grid item xs={8} sx={{ height: "2.85rem" }}>
                      <Controller
                        name="customerTypePref"
                        control={control}
                        render={({ field, fieldState }) => (
                          <CustomSelectBox
                            {...field}
                            error={Boolean(fieldState.error)}
                            value={field.value?.[0] || "RETAIL"}
                            onChange={(e) => {
                              const next = e.target.value as
                                | "RETAIL"
                                | "BUSINESS"
                                | "EMPLOYEE";
                              const current = (getValues(
                                "customerTypePref"
                              )?.[0] || "RETAIL") as
                                | "RETAIL"
                                | "BUSINESS"
                                | "EMPLOYEE";

                              if (isRestricted(next) && next !== current) {
                                setPendingType(next);
                                setMgrOpen(true);

                                setValue("customerTypePref", ["RETAIL"], {
                                  shouldDirty: false,
                                });
                                return;
                              }

                              field.onChange([next]);
                            }}
                            formControlProps={{
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  justifyContent: "space-between",
                                  textAlign: "left",
                                },
                                "& .MuiInputBase-root": {
                                  justifyContent: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.157rem",
                                  pl: "0.4rem",
                                },
                                "& fieldset": {
                                  borderRadius: "10px",
                                  border: "none",
                                },
                              },
                            }}
                            defaultValue="RETAIL"
                            tabIndex={40}
                            ref={inputRef[39]}
                            inputProps={{
                              tabIndex: 40,
                              onBlur: (event: any) =>
                                handleTabAndEnter(inputRef, event, 39),
                            }}
                          >
                            <MenuItem value="RETAIL">Retail Customer</MenuItem>
                            <MenuItem value="BUSINESS">
                              Business Customer
                            </MenuItem>
                            <MenuItem value="EMPLOYEE">Employee</MenuItem>
                          </CustomSelectBox>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Payment Preference */}
                  <Grid container sx={{ mt: "0.57rem", alignItems: "center" }}>
                    <Grid item xs={4}>
                      <HeaderTitle
                        variation="field_label"
                        title="Payment Preference"
                      />
                    </Grid>
                    <Grid item xs={8} sx={{ height: "2.85rem" }}>
                      <Controller
                        name="paymentPreferencePref"
                        control={control}
                        render={({ field, fieldState }) => (
                          <CustomSelectBox
                            {...field}
                            error={Boolean(fieldState.error)}
                            value={field.value || []}
                            onChange={(e) =>
                              field.onChange(
                                typeof e.target.value === "string"
                                  ? e.target.value.split(",")
                                  : e.target.value
                              )
                            }
                            formControlProps={{
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  justifyContent: "space-between",
                                  textAlign: "left",
                                },
                                "& .MuiInputBase-root": {
                                  justifyContent: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.157rem",
                                  pl: "0.4rem",
                                },
                                "& fieldset": {
                                  borderRadius: "10px",
                                  border: "none",
                                },
                              },
                            }}
                            defaultValue="DROP_OFF"
                            tabIndex={41}
                            ref={inputRef[40]}
                            inputProps={{
                              tabIndex: 41,
                              onBlur: (event) => {
                                handleTabAndEnter(inputRef, event, 40);
                              },
                            }}
                          >
                            <MenuItem value="DROP_OFF">Pre-Pay</MenuItem>
                            <MenuItem value="ANYTIME">Anytime</MenuItem>
                          </CustomSelectBox>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Pay At */}
                  <Grid container sx={{ mt: "0.57rem", alignItems: "center" }}>
                    <Grid item xs={4}>
                      <HeaderTitle variation="field_label" title="Pay At" />
                    </Grid>
                    <Grid item xs={8} sx={{ height: "2.85rem" }}>
                      <Controller
                        name="customerPayAtPref"
                        control={control}
                        render={({ field, fieldState }) => (
                          <CustomSelectBox
                            {...field}
                            error={Boolean(fieldState.error)}
                            value={field.value || []}
                            onChange={(e) =>
                              field.onChange(
                                typeof e.target.value === "string"
                                  ? e.target.value.split(",")
                                  : e.target.value
                              )
                            }
                            formControlProps={{
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiSelect-select": {
                                  display: "flex",
                                  justifyContent: "space-between",
                                  textAlign: "left",
                                },
                                "& .MuiInputBase-root": {
                                  justifyContent: "left",
                                },
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "0.157rem",
                                  pl: "0.4rem",
                                },
                                "& fieldset": {
                                  borderRadius: "10px",
                                  border: "none",
                                },
                              },
                            }}
                            defaultValue="COUNTER"
                            tabIndex={42}
                            ref={inputRef[41]}
                            inputProps={{
                              tabIndex: 42,
                              onBlur: (event) => {
                                handleTabAndEnter(inputRef, event, 41);
                              },
                            }}
                          >
                            {preferenceStatus === "DROP_OFF" && (
                              <MenuItem value="COUNTER">Counter</MenuItem>
                            )}

                            {preferenceStatus === "ANYTIME" && (
                              <MenuItem value="COUNTER">Counter</MenuItem>
                            )}

                            {preferenceStatus === "ANYTIME" && (
                              <MenuItem value="CARD_ON_FILE">
                                Card on file
                              </MenuItem>
                            )}
                            {preferenceStatus === "ANYTIME" && (
                              <MenuItem value="MONTHLY_CHARGE">
                                Monthly charge
                              </MenuItem>
                            )}
                          </CustomSelectBox>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Enable Delivery */}
                  <Grid container sx={{ mt: "1.2rem", alignItems: "center" }}>
                    <Grid item xs={4}>
                      <HeaderTitle
                        variation="field_label"
                        title="Enable Delivery"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Controller
                        name="isDeliveryEnabled"
                        control={control}
                        render={({ field }) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <IOSSwitch
                              {...field}
                              checked={field.value || false}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                field.onChange(isChecked);

                                if (isChecked) {
                                  const isVerifiedMock = false; // Change to true for testing verified behavior
                                  setIsAddressVerified(isVerifiedMock);
                                  setIsManualOverride(false);

                                  const defaultRoute = routeOptions[0];
                                  if (defaultRoute) {
                                    setSelectedRoute(defaultRoute.value);
                                    setDeliveryDays(defaultRoute.days);
                                  }
                                } else {
                                  setIsAddressVerified(null);
                                  setIsManualOverride(false);
                                  setSelectedRoute("");
                                  setDeliveryDays("");
                                }
                              }}
                              tabIndex={43}
                              ref={inputRef[42]}
                              onKeyDown={(event) =>
                                handleTabAndEnter(inputRef, event, 42)
                              }
                              sx={{
                                "& .MuiSwitch-switchBase.Mui-checked": {
                                  color: Colors.SECONDARY,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: Colors.SECONDARY,
                                },
                                "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb":
                                {
                                  color: Colors.WHITE,
                                },
                                "& .MuiSwitch-switchBase": {
                                  padding: 0,
                                  margin: "0.23214283rem",
                                },
                                "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                                  border: "none",
                                },
                              }}
                            />
                            {isAddressVerified === true &&
                              !isManualOverride && (
                                <Typography
                                  sx={{
                                    color: Colors.PRIMARY,
                                    fontSize: pxToRem(16),
                                    marginLeft: pxToRem(30),
                                  }}
                                >
                                  Address Verified!
                                </Typography>
                              )}

                            {isAddressVerified === false && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  marginLeft: pxToRem(30),
                                  gap: pxToRem(40),
                                }}
                              >
                                <Typography
                                  sx={{
                                    color: Colors.BROWN,
                                    fontSize: pxToRem(16),
                                  }}
                                >
                                  Couldn’t verify!
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: pxToRem(15),
                                    color: Colors.BLACK,
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => setShowAssignRouteModal(true)}
                                >
                                  Assign Anyway?
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        )}
                      />
                    </Grid>
                  </Grid>

                  {isAddressVerified === true && !isManualOverride && (
                    <>
                      <Grid
                        container
                        sx={{ mt: "1.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Route"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <CustomTextBox
                            disabled
                            value={selectedRoute}
                            sx={customTextBoxStyles}
                          />
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        sx={{ mt: "0.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Days"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <CustomTextBox
                            disabled
                            value={deliveryDays}
                            sx={customTextBoxStyles}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* Always show Delivery Route and Delivery Days if delivery is enabled */}
                  {watch("isDeliveryEnabled") && (
                    <>
                      <Grid
                        container
                        sx={{ mt: "1.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Route"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          {isManualOverride ? (
                            <CustomSelectBox
                              value={selectedRoute}
                              onChange={(e) => {
                                const route = routeOptions.find(
                                  (r) => r.value === e.target.value
                                );
                                setSelectedRoute(route?.value || "");
                                setDeliveryDays(route?.days || "");
                              }}
                              formControlProps={{
                                sx: {
                                  width: "100%",
                                  backgroundColor: Colors.WHITE,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                  },
                                  "& fieldset": {
                                    border: "none",
                                  },
                                },
                              }}
                            >
                              {routeOptions.map((route) => (
                                <MenuItem value={route.value} key={route.value}>
                                  {route.label}
                                </MenuItem>
                              ))}
                            </CustomSelectBox>
                          ) : (
                            <CustomTextBox
                              disabled
                              value={selectedRoute}
                              sx={customTextBoxStyles}
                            />
                          )}
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        sx={{ mt: "0.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Days"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <CustomTextBox
                            disabled
                            value={deliveryDays}
                            sx={customTextBoxStyles}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* {isAddressVerified === true && isManualOverride && (
                    <>
                      <Grid
                        container
                        sx={{ mt: "1.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Route"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <CustomSelectBox
                            value={selectedRoute}
                            onChange={(e) => {
                              const route = routeOptions.find(
                                (r) => r.value === e.target.value
                              );
                              setSelectedRoute(route?.value || "");
                              setDeliveryDays(route?.days || "");
                            }}
                            formControlProps={{
                              sx: {
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "10px",
                                },
                                "& fieldset": {
                                  border: "none",
                                },
                              },
                            }}
                          >
                            {routeOptions.map((route) => (
                              <MenuItem value={route.value} key={route.value}>
                                {route.label}
                              </MenuItem>
                            ))}
                          </CustomSelectBox>
                        </Grid>
                      </Grid>

                      <Grid
                        container
                        sx={{ mt: "0.57rem", alignItems: "center" }}
                      >
                        <Grid item xs={4}>
                          <HeaderTitle
                            variation="field_label"
                            title="Delivery Days"
                          />
                        </Grid>
                        <Grid item xs={8}>
                          <CustomTextBox
                            disabled
                            value={deliveryDays}
                            sx={customTextBoxStyles}
                          />
                        </Grid>
                      </Grid>
                    </>
                  )} */}

                  {showAssignRouteModal && (
                    <AssignNewRoute
                      onSuccess={() => {
                        setIsAddressVerified(true);
                        setIsManualOverride(true);
                        setShowAssignRouteModal(false);
                      }}
                    >
                      <></>
                    </AssignNewRoute>
                  )}

                  {/* Automatic Alteration */}
                  <Grid container sx={{ mt: "2.5rem", alignItems: "center" }}>
                    <Grid item xs={4} sx={{ display: "flex", alignItems: "center" }}>
                      <HeaderTitle
                        variation="field_label"
                        title="Automatic Alteration"
                      />
                    </Grid>
                    <Grid item xs={8}>
                      <Controller
                        name="automaticAlteration"
                        control={control}
                        render={({ field }) => (
                          <IOSSwitch
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                            tabIndex={51}
                            ref={inputRef[50]}
                            onKeyDown={(event) =>
                              handleTabAndEnter(inputRef, event, 50)
                            }
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: Colors.SECONDARY,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                              {
                                backgroundColor: Colors.SECONDARY,
                              },
                              "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb":
                              {
                                color: Colors.WHITE,
                              },
                              "& .MuiSwitch-switchBase": {
                                padding: 0,
                                margin: "0.23214283rem",
                              },
                              "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                                border: "none",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  {/* Allowed Amount */}
                  {watch("automaticAlteration") && (
                    <Grid container sx={{ mt: "1.2rem", alignItems: "center" }}>
                      <Grid item xs={4}>
                        <HeaderTitle
                          variation="field_label"
                          title="Allowed Amount"
                        />
                      </Grid>
                      <Grid item xs={8}>
                        <Controller
                          name="allowedAmount"
                          control={control}
                          render={({ field, fieldState }) => (
                            <CustomTextBox
                              {...field}
                              value={field.value ?? ""}
                              type="number"
                              error={fieldState.invalid}
                              helperText={fieldState.error?.message}
                              tabIndex={52}
                              inputRef={inputRef[51]}
                              onKeyDown={(event) =>
                                handleTabAndEnter(inputRef, event, 51)
                              }
                              onBlur={() => {
                                const parsed = parseFloat(field.value ?? "");
                                field.onChange(
                                  isNaN(parsed) ? "" : parsed.toFixed(2)
                                );
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              }}
                              inputProps={{ step: "0.01", min: "0" }}
                              sx={{
                                width: "100%",
                                backgroundColor: Colors.WHITE,
                                "& .MuiOutlinedInput-root": {
                                  height: "2.857rem",
                                  padding: "0 0 0 0.857rem",
                                  alignItems: "center",
                                  fontSize: "1.14rem",
                                },
                                "& .MuiInputBase-input": {
                                  height: "100%",
                                  padding: "0 0.857rem 0 0.5rem",
                                  boxSizing: "border-box",
                                  textAlign: "left",
                                  fontSize: "1.14rem",
                                },
                                "& .MuiInputAdornment-root p": {
                                  fontSize: "1.14rem",
                                },
                                "& fieldset": {
                                  border: "none !important",
                                  borderRadius: "10px !important",
                                },
                              }}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  )}

                  {/* Customer Type */}
                </PanelLayout>
                {/* Preferences */}
              </Grid>
              <Grid
                sx={{
                  border: "none",
                  background: Colors.BACKGROUND_GRAY,
                  borderRadius: "1.42rem",
                  mt: "0.71rem",
                  mb: "0.5rem",
                  marginLeft: "0.5rem",
                  p: "1rem",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "10.13rem",
                  padding: "0.57rem",
                }}
              >
                <CustomButton
                  type="submit"
                  name="submitCustomerForm"
                  data-testid="submit-customer-button"
                  data-field-name="submitCustomerForm"
                  aria-label={
                    mode === "edit" ? "Update Customer" : "Add New Customer"
                  }
                  onClick={handleCustomSubmit}
                  sx={{
                    width: "28.3571428571rem",
                    height: "5.85714285714rem",
                    borderRadius: "0.71rem",
                    padding: "1.64285714rem",
                    fontSize: "2.28rem",
                    alignSelf: "center",
                    marginLeft: "1rem",
                  }}
                  tabIndex={44}
                  ref={inputRef[43]}
                  // onKeyDown={(event) =>
                  //   handleTabAndEnter(inputRef, event, 42)
                  // }
                  disabled={
                    isLoading ||
                    !isValid ||
                    (!isDirty && !fromInactivePopup) ||
                    isEmailInvalid
                  }
                >
                  {isLoading ? (
                    <CircularProgress
                      sx={{ color: Colors.WHITE, width: 5, height: 5 }}
                    />
                  ) : mode === "edit" ? (
                    fromInactivePopup ? (
                      "Confirm Details"
                    ) : (
                      "Update Customer"
                    )
                  ) : (
                    "Add New Customer"
                  )}
                </CustomButton>
              </Grid>
            </Grid>
          </Grid>
        </form>
        <ManagerCodeModal
          open={mgrOpen}
          onClose={() => {
            setMgrOpen(false);
            setPendingType(null);
          }}
          title="Manager Approval"
          task="change the customer type"
          onDone={() => {
            if (pendingType) {
              setValue("customerTypePref", [pendingType], {
                shouldDirty: true,
              });
            }
            setMgrOpen(false);
            setPendingType(null);
          }}
          actionType="CHANGE_CUSTOMER_TYPE"
          requestedByUserId={user?.id}
        />
      </PosInnerPageLayout>
    </PosLayout>
  );
};

export default CreateCustomerPage;
